import { Button, Grid, TextField, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { HttpClient } from 'src/Helpers/http/http';
import Toaster from 'src/Toaster/Toaster';
import { FormValues } from './types';

type CreateVideoUrlProps = {
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

interface IApiResponse {
  success: boolean;
  message: string;
}

function CreateVideoUrl({ handleClose, setRefreshTrigger }: CreateVideoUrlProps) {
  const theme = useTheme();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  const [loading, setLoading] = useState<boolean>(false);
  const Toast = Toaster();

  const onSubmit = async (values: FormValues) => {
    try {
      setLoading(true);

      const payload = {
        url: values.url,
        order: Number(values.order),
      };

      const response = await HttpClient.post<IApiResponse>(
        '/api/v1/admin/video-url',
        payload
      );

      if (response.success) {
        Toast.fire({
          icon: 'success',
          title: response.message || 'Video URL created successfully!',
        });
        setRefreshTrigger((prev) => prev + 1);
        reset();
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: response.message || 'Failed to create Video URL.',
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: `${err?.response?.data?.message || 'Something went wrong'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1, p:1}}>
      <Grid container spacing={2}>
        {/* Video URL */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='url'
            label='Video URL'
            placeholder='e.g. https://www.youtube.com/embed/...'
            type='text'
            autoFocus
            variant='outlined'
            error={!!errors.url}
            helperText={errors.url?.message}
            {...register('url', { required: 'Video URL is required' })}
          />
          <Box
            sx={{
              backgroundColor: theme.colors?.info?.lighter || 'rgba(0, 163, 255, 0.08)',
              borderLeft: `4px solid ${theme.colors?.info?.main || '#00a3ff'}`,
              borderRadius: '4px',
              p: 1.5,
              mt: 1,
            }}
          >
            <Typography
              variant='body2'
              sx={{
                fontWeight: 700,
                color: theme.colors?.info?.main || '#00a3ff',
                mb: 0.5,
              }}
            >
              YouTube Link Tips:
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.6 }}>
              Please provide a valid YouTube video link For example:
              <br />
              • <code>https://www.youtube.com/watch?v=VIDEO_ID</code>
            </Typography>
          </Box>
        </Grid>

        {/* Order */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='order'
            label='Display Order'
            type='number'
            inputProps={{
              min: 1,
              step: 1,
            }}
            variant='outlined'
            error={!!errors.order}
            helperText={errors.order?.message}
            {...register('order', {
              required: 'Display order is required',
              valueAsNumber: false,
            })}
          />
        </Grid>

        {/* Action Button */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Creating...' : 'Create Video URL'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CreateVideoUrl;
